import { _PdfDictionary, _PdfName, _PdfReference } from './../pdf-primitives';
import { PdfField, PdfTextBoxField, PdfButtonField, PdfCheckBoxField, PdfRadioButtonListField, PdfComboBoxField, PdfListBoxField, PdfSignatureField } from './field';
import { _getInheritableProperty, _getPageIndex, _isNullOrUndefined } from './../utils';
import { PdfFormFieldsTabOrder, _FieldFlag, _SignatureFlag } from './../enumerator';
import { PdfPage } from './../pdf-page';
import { PdfAnnotationCollection } from './../annotations/annotation-collection';
import { PdfWidgetAnnotation } from './../annotations/annotation';
/**
 * Represents a PDF form.
 * ```typescript
 * // Load an existing PDF document
 * let document: PdfDocument = new PdfDocument(data, password);
 * // Access the form of the PDF document
 * let form: PdfForm = document.form;
 * // Save the document
 * document.save('output.pdf');
 * // Destroy the document
 * document.destroy();
 * ```
 */
export class PdfForm {
    /**
     * Represents a loaded from the PDF document.
     *
     * @private
     * @param {_PdfDictionary} dictionary Form dictionary.
     * @param {_PdfCrossReference} crossReference Cross reference object.
     */
    constructor(dictionary, crossReference) {
        this._isDefaultAppearance = false;
        this._hasKids = false;
        this._setAppearance = false;
        this._exportEmptyFields = false;
        this._fieldCollection = [];
        this._signFlag = _SignatureFlag.none;
        this._isNeedAppearances = false;
        this._formNames = [];
        this._dictionary = dictionary;
        this._crossReference = crossReference;
        this._parsedFields = new Map();
        this._fields = [];
        this._createFields();
    }
    /**
     * Gets the fields count (Read only).
     *
     * @returns {number} Fields count.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Access loaded form
     * let form: PdfForm = document.form;
     * // Gets the fields count
     * let count: number = form.count;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    get count() {
        return this._fields.length;
    }
    /**
     *  Gets a value indicating whether need appearances (Read only).
     *
     * @returns {boolean} Need appearances.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Access loaded form
     * let form: PdfForm = document.form;
     * // Gets the boolean flag indicating need appearances
     * let needAppearances: number = form.needAppearances;
     * // Destroy the document
     * document.destroy();
     * ```
     */
    get needAppearances() {
        if (this._dictionary.has('NeedAppearances')) {
            this._needAppearances = this._dictionary.get('NeedAppearances');
        }
        return this._needAppearances;
    }
    /**
     *  Gets a value indicating whether allow to export empty fields or not.
     *
     * @returns {boolean} Export empty fields.
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Access loaded form
     * let form: PdfForm = document.form;
     * // Gets a value indicating whether allow to export empty fields or not.
     * let exportEmptyFields: boolean = form.exportEmptyFields;
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    get exportEmptyFields() {
        return this._exportEmptyFields;
    }
    /**
     *  Sets a value indicating whether allow to export empty fields or not.
     *
     * @param {boolean} value Export empty fields.
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Access loaded form
     * let form: PdfForm = document.form;
     * // Sets a value indicating whether allow to export empty fields or not.
     * form.exportEmptyFields = false;
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    set exportEmptyFields(value) {
        this._exportEmptyFields = value;
    }
    get _signatureFlag() {
        return this._signFlag;
    }
    set _signatureFlag(value) {
        if (value !== this._signFlag) {
            this._signFlag = value;
            this._dictionary.update('SigFlags', value);
        }
    }
    /**
     * Gets the `PdfField` at the specified index.
     *
     * @param {number} index Field index.
     * @returns {PdfField} Loaded PDF form field at the specified index.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Access the loaded form field
     * let field: PdfField = document.form.fieldAt(0);
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    fieldAt(index) {
        if (index < 0 || index >= this._fields.length) {
            throw Error('Index out of range.');
        }
        let field;
        if (this._parsedFields.has(index)) {
            field = this._parsedFields.get(index);
            this._isNeedAppearances = true;
        }
        else {
            let dictionary;
            const ref = this._fields[index]; // eslint-disable-line
            if (ref && ref instanceof _PdfReference) {
                dictionary = this._crossReference._fetch(ref);
            }
            if (dictionary) {
                field = this._parseFields(dictionary, ref);
                this._parsedFields.set(index, field);
                if (field && field instanceof PdfField) {
                    field._annotationIndex = index;
                }
            }
        }
        return field;
    }
    _parseFields(dictionary, reference) {
        let field;
        if (dictionary) {
            const key = _getInheritableProperty(dictionary, 'FT', false, true, 'Parent');
            let fieldFlags = 0;
            const flag = _getInheritableProperty(dictionary, 'Ff', false, true, 'Parent');
            if (typeof flag !== 'undefined') {
                fieldFlags = flag;
            }
            if (key) {
                switch (key.name.toLowerCase()) {
                    case 'tx':
                        field = PdfTextBoxField._load(this, dictionary, this._crossReference, reference);
                        break;
                    case 'btn':
                        if ((fieldFlags & _FieldFlag.pushButton) !== 0) {
                            field = PdfButtonField._load(this, dictionary, this._crossReference, reference);
                        }
                        else if ((fieldFlags & _FieldFlag.radio) !== 0) {
                            field = PdfRadioButtonListField._load(this, dictionary, this._crossReference, reference);
                        }
                        else {
                            field = PdfCheckBoxField._load(this, dictionary, this._crossReference, reference);
                        }
                        break;
                    case 'ch':
                        if ((fieldFlags & _FieldFlag.combo) !== 0) {
                            field = PdfComboBoxField._load(this, dictionary, this._crossReference, reference);
                        }
                        else {
                            field = PdfListBoxField._load(this, dictionary, this._crossReference, reference);
                        }
                        break;
                    case 'sig':
                        field = PdfSignatureField._load(this, dictionary, this._crossReference, reference);
                        break;
                }
            }
        }
        return field;
    }
    /**
     * Add a new `PdfField`.
     *
     * @param {PdfField} field Field object to add.
     * @returns {number} Field index.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Add a new form field
     * let index: number = document.form.add(field);
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    add(field) {
        this._fields.push(field._ref);
        this._dictionary.update('Fields', this._fields);
        this._parsedFields.set(this._fields.length - 1, field);
        field._form = this;
        this._crossReference._root._updated = true;
        if (field instanceof PdfSignatureField) {
            field._form._signatureFlag = _SignatureFlag.signatureExists | _SignatureFlag.appendOnly;
        }
        this._isNeedAppearances = true;
        return (this._fields.length - 1);
    }
    /**
     * Remove the specified PDF form field.
     *
     * @param {PdfField} field Field object to remove.
     * @returns {void} Nothing.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Access the loaded form field
     * let field: PdfField = document.form.fieldAt(3);
     * // Remove the form field
     * document.form.removeField(field);
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    removeField(field) {
        const index = this._fields.indexOf(field._ref);
        if (index >= 0) {
            this.removeFieldAt(index);
        }
    }
    /**
     * Remove the PDF form field from specified index.
     *
     * @param {number} index Field index to remove.
     * @returns {void} Nothing.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Remove the form field from the specified index
     * document.form.removeFieldAt(3);
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    removeFieldAt(index) {
        const field = this.fieldAt(index);
        if (field) {
            if (field._kidsCount > 0) {
                for (let i = field._kidsCount - 1; i >= 0; i--) {
                    const item = field.itemAt(i);
                    let page;
                    if (item) {
                        page = item._getPage();
                        if (page) {
                            page._removeAnnotation(item._ref);
                        }
                    }
                }
            }
            else if (field._dictionary.has('Subtype') && field._dictionary.get('Subtype').name === 'Widget') {
                const page = field.page;
                if (page) {
                    page._removeAnnotation(field._ref);
                }
            }
            this._parsedFields.delete(index);
            this._reorderParsedAnnotations(index);
        }
        this._fields.splice(index, 1);
        const document = this._crossReference._document;
        const catalog = document._catalog;
        if (this._fields.length === 0 && document && catalog && catalog._catalogDictionary) {
            catalog._catalogDictionary._updated = true;
            this._crossReference._allowCatalog = true;
        }
        this._dictionary.set('Fields', this._fields);
        this._dictionary._updated = true;
    }
    _reorderParsedAnnotations(index) {
        const result = new Map();
        this._parsedFields.forEach((value, key) => {
            if (key > index) {
                result.set(key - 1, value);
            }
            else {
                result.set(key, value);
            }
        });
        this._parsedFields = result;
    }
    /**
     * Sets the flag to indicate the new appearance creation
     * If true, appearance will not be created. Default appearance has been considered.
     * If false, new appearance stream has been created from field values and updated as normal appearance.
     *
     * @param {boolean} value Set default appearance.
     * @returns {void} Nothing.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Set boolean flag to create a new appearance stream for form fields.
     * document.form.setDefaultAppearance(false);
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    setDefaultAppearance(value) {
        this._setAppearance = !value;
        this._needAppearances = value;
        this._isDefaultAppearance = value;
    }
    orderFormFields(tabOrder) {
        if (tabOrder === null || typeof tabOrder === 'undefined') {
            this.orderFormFields(new Map());
        }
        else {
            let tab;
            const document = this._crossReference._document;
            let value;
            if (tabOrder && tabOrder instanceof Map) {
                let setTabOrder = true;
                if (tabOrder.size > 0) {
                    this._tabCollection = tabOrder;
                }
                else {
                    setTabOrder = false;
                    this._tabCollection = tabOrder;
                }
                const fieldCollection = new Map();
                this._fieldCollection = this._getFields();
                if (_isNullOrUndefined(this._fieldCollection) && this._fieldCollection.length > 0) {
                    const page = this._fieldCollection[0].page;
                    if (page && document) {
                        this._fieldCollection.forEach((field) => {
                            if (field.page) {
                                const index = _getPageIndex(document, this._sortItemByPageIndex(field, true)._pageDictionary);
                                if (index >= 0) {
                                    if (fieldCollection.has(index)) {
                                        value = fieldCollection.get(index);
                                        value.push(field);
                                    }
                                    else {
                                        value = [];
                                        value.push(field);
                                        fieldCollection.set(index, value);
                                    }
                                    const page = document.getPage(index);
                                    if (!this._tabCollection.has(index)) {
                                        this._tabCollection.set(index, page.tabOrder);
                                    }
                                    if (setTabOrder) {
                                        page.tabOrder = this._tabCollection.get(index);
                                    }
                                }
                            }
                        });
                        let fieldsCount = 0;
                        fieldCollection.forEach((value, key) => {
                            this._tabOrder = this._tabCollection.get(key);
                            if (this._tabOrder !== PdfFormFieldsTabOrder.structure) {
                                const fields = value;
                                fields.sort((pdfField1, pdfField2) => {
                                    return this._compareFields(pdfField1, pdfField2);
                                });
                                fields.forEach((field, j) => {
                                    const fieldIndex = this._fieldCollection.indexOf(field);
                                    if (fieldIndex !== -1 && fieldIndex !== fieldsCount + j) {
                                        const fieldToMove = this._fieldCollection[fieldIndex];
                                        this._fieldCollection.splice(fieldIndex, 1);
                                        this._fieldCollection.splice(fieldsCount + j, 0, fieldToMove);
                                    }
                                });
                            }
                            fieldsCount += value.length;
                        });
                    }
                }
            }
            else {
                this._tabOrder = tabOrder;
                tab = this._getOrder(this._tabOrder);
                this._fieldCollection = this._getFields();
                this._fieldCollection.sort((pdfField1, pdfField2) => {
                    return this._compareFields(pdfField1, pdfField2);
                });
            }
            this._parsedFields.clear();
            this._fieldCollection.forEach((field, i) => {
                this._parsedFields.set(i, field);
                this._fields[i] = field._ref;
                if (tab) {
                    field.page._pageDictionary.update('Tabs', tab);
                }
            });
            this._dictionary.update('Fields', this._fields);
        }
    }
    _createFields() {
        let fields; // eslint-disable-line
        const fieldsMap = new Map();
        if (this._dictionary && this._dictionary.has('Fields')) {
            fields = this._dictionary.get('Fields');
        }
        let count = 0;
        const nodes = []; // eslint-disable-line
        const terminalFields = [];
        while (fields && fields.length > 0) {
            for (; count < fields.length; count++) {
                const ref = fields[count]; // eslint-disable-line
                let fieldDictionary;
                if (ref && ref instanceof _PdfReference) {
                    fieldDictionary = this._crossReference._fetch(ref);
                    if (fieldDictionary) {
                        fieldsMap.set(fieldDictionary, ref);
                    }
                }
                let fieldKids;
                if (fieldDictionary && fieldDictionary.has('Kids')) {
                    fieldKids = fieldDictionary.get('Kids');
                    if (fieldKids && fieldKids.length > 0) {
                        fieldKids.forEach((reference) => {
                            if (reference instanceof _PdfReference) {
                                const kidsDict = this._crossReference._fetch(reference);
                                if (kidsDict) {
                                    fieldsMap.set(kidsDict, reference);
                                    if (!kidsDict.has('Parent')) {
                                        kidsDict.update('Parent', ref);
                                    }
                                }
                            }
                        });
                    }
                }
                if (!fieldKids) {
                    if (fieldDictionary) {
                        if (terminalFields.indexOf(fieldDictionary) === -1) {
                            terminalFields.push(fieldDictionary);
                            if (fieldDictionary.has('T')) {
                                const fieldName = fieldDictionary.get('T');
                                if (this._formNames.indexOf(fieldName) === -1) {
                                    this._formNames.push(fieldName);
                                }
                            }
                        }
                    }
                }
                else {
                    const isNode = (!fieldDictionary.has('FT')) || this._isNode(fieldKids);
                    if (isNode) {
                        nodes.push({ fields, count });
                        this._hasKids = true;
                        count = -1;
                        fields = fieldKids;
                    }
                    else {
                        terminalFields.push(fieldDictionary);
                        if (fieldDictionary.has('T')) {
                            const fieldName = fieldDictionary.get('T');
                            if (this._formNames.indexOf(fieldName) === -1) {
                                this._formNames.push(fieldName);
                            }
                        }
                    }
                }
            }
            if (nodes.length === 0) {
                break;
            }
            const entry = nodes.pop(); // eslint-disable-line
            fields = entry.fields;
            count = entry.count + 1;
        }
        this._createFieldCollection(terminalFields, fieldsMap);
    }
    _createFieldCollection(terminalFields, fieldsMap) {
        const pageWidgets = new Map();
        const document = this._crossReference._document;
        const widgetCollection = [];
        if (document) {
            for (let i = 0; i < document.pageCount; i++) {
                const page = document.getPage(i);
                if (!page || !page._pageDictionary) {
                    continue;
                }
                const pageDictionary = page._pageDictionary;
                let widgetAnnots = [];
                if (pageDictionary && pageDictionary.has('Annots')) {
                    widgetAnnots = pageDictionary.getRaw('Annots');
                    if (widgetAnnots instanceof _PdfReference) {
                        widgetAnnots = this._crossReference._fetch(widgetAnnots);
                    }
                }
                const widgets = [];
                for (let j = 0; j < widgetAnnots.length; j++) {
                    const ref = widgetAnnots[j];
                    if (ref && ref instanceof _PdfReference) {
                        widgetCollection.push(ref);
                        const annotDictionary = this._crossReference._fetch(ref);
                        if (annotDictionary && annotDictionary.has('Subtype') &&
                            annotDictionary.get('Subtype').name === 'Widget') {
                            widgets.push(annotDictionary);
                        }
                    }
                }
                pageWidgets.set(i, widgets);
            }
        }
        for (let i = 0; i < terminalFields.length; i++) {
            const fieldDictionary = terminalFields[i];
            const fieldRef = fieldsMap.get(fieldDictionary);
            if (fieldRef && this._removeInvalidFields(fieldDictionary, pageWidgets, fieldRef, widgetCollection)) {
                if (this._fields.indexOf(fieldRef) === -1) {
                    this._fields.push(fieldRef);
                }
            }
        }
        if (terminalFields.length > 0) {
            this._processRemainingWidgets();
        }
    }
    _processRemainingWidgets() {
        const document = this._crossReference._document;
        for (let i = 0; i < document.pageCount; i++) {
            const page = document.getPage(i);
            if (!page || !page._pageDictionary) {
                continue;
            }
            const pageDictionary = page._pageDictionary;
            let widgetAnnots = [];
            if (pageDictionary.has('Annots')) {
                widgetAnnots = pageDictionary.getRaw('Annots');
                if (widgetAnnots instanceof _PdfReference) {
                    widgetAnnots = this._crossReference._fetch(widgetAnnots);
                }
            }
            for (let j = 0; j < widgetAnnots.length; j++) {
                const ref = widgetAnnots[j];
                if (ref && ref instanceof _PdfReference && this._fields.indexOf(ref) === -1) {
                    const annotDictionary = this._crossReference._fetch(ref);
                    if (annotDictionary &&
                        annotDictionary.has('FT') &&
                        annotDictionary.has('Subtype') &&
                        annotDictionary.get('Subtype').name === 'Widget' &&
                        annotDictionary.has('T') && annotDictionary.has('P') &&
                        this._formNames.indexOf(annotDictionary.get('T')) === -1) {
                        this._fields.push(ref);
                    }
                }
            }
        }
    }
    _hasValidKids(dictionary) {
        const kidsArray = dictionary.get('Kids'); // eslint-disable-line
        return kidsArray && kidsArray.length > 0;
    }
    _removeInvalidFields(dictionary, pageWidgets, ref, widgetCollection) {
        if (!dictionary) {
            return false;
        }
        if (dictionary.has('Kids') && this._hasValidKids(dictionary)) {
            const kidsArray = dictionary.get('Kids'); // eslint-disable-line
            const invalidKids = [];
            let hasValidChild = false;
            for (let i = 0; i < kidsArray.length; i++) {
                let childDictionary;
                const kidRef = kidsArray[i]; // eslint-disable-line
                if (kidRef instanceof _PdfReference) {
                    childDictionary = this._crossReference._fetch(kidRef);
                }
                else if (kidRef instanceof _PdfDictionary) {
                    childDictionary = kidRef;
                }
                if (childDictionary) {
                    if (childDictionary.has('P')) {
                        const pageRef = childDictionary.get('P'); // eslint-disable-line
                        let pageDictionary;
                        if (pageRef instanceof _PdfReference) {
                            pageDictionary = this._crossReference._fetch(pageRef);
                        }
                        else if (pageRef instanceof _PdfDictionary) {
                            pageDictionary = pageRef;
                        }
                        if (pageDictionary && pageDictionary.has('Annots')) {
                            let annots = pageDictionary.get('Annots'); // eslint-disable-line
                            if (annots instanceof _PdfReference) {
                                annots = this._crossReference._fetch(annots);
                            }
                            if (annots && Array.isArray(annots)) {
                                const kidExists = annots.indexOf(kidRef) !== -1;
                                if (!kidExists) {
                                    invalidKids.push(i);
                                    continue;
                                }
                            }
                        }
                    }
                    const isChildValid = this._removeInvalidFields(childDictionary, pageWidgets, kidRef, widgetCollection);
                    if (isChildValid) {
                        hasValidChild = true;
                    }
                    else {
                        invalidKids.push(i);
                    }
                }
                else {
                    invalidKids.push(i);
                }
            }
            for (let i = invalidKids.length - 1; i >= 0; i--) {
                kidsArray.splice(invalidKids[i], 1);
            }
            if (kidsArray.length > 0 && hasValidChild) {
                dictionary.update('Kids', kidsArray);
                return true;
            }
            return false;
        }
        else {
            return this._validateField(dictionary, pageWidgets, ref, widgetCollection);
        }
    }
    _validateField(fieldDictionary, pageWidgets, ref, widgetCollection) {
        if (!fieldDictionary || !pageWidgets) {
            return false;
        }
        if (fieldDictionary.has('P') && fieldDictionary.has('Rect')) {
            return true;
        }
        if (widgetCollection && widgetCollection.indexOf(ref) !== -1) {
            return true;
        }
        let found = false;
        pageWidgets.forEach((widgets, pageIndex) => {
            if (found) {
                return;
            }
            if (widgets && widgets.length > 0) {
                for (let j = 0; j < widgets.length; j++) {
                    const widget = widgets[j];
                    if (widget && this._compareWidgets(widget, fieldDictionary)) {
                        if (this._crossReference && this._crossReference._document) {
                            const page = this._crossReference._document.getPage(pageIndex);
                            if (page && page._ref && fieldDictionary.has('FT')) {
                                fieldDictionary.update('P', page._ref);
                                found = true;
                                return;
                            }
                        }
                    }
                }
            }
        });
        return found;
    }
    _compareWidgets(widget, annotDictionary) {
        if (!widget || !annotDictionary) {
            return false;
        }
        let widgetType;
        let annotType;
        if (widget.has('FT')) {
            widgetType = widget.get('FT');
        }
        if (annotDictionary.has('FT')) {
            annotType = annotDictionary.get('FT');
        }
        if (widgetType && annotType && widgetType.name !== annotType.name) {
            return false;
        }
        let widgetName;
        let annotName;
        if (widget.has('T')) {
            widgetName = widget.get('T');
        }
        if (annotDictionary.has('T')) {
            annotName = annotDictionary.get('T');
        }
        if (widgetName && annotName && widgetName !== annotName) {
            return false;
        }
        let widgetValue;
        let annotValue;
        if (widget.has('V')) {
            widgetValue = widget.get('V');
        }
        if (annotDictionary.has('V')) {
            annotValue = annotDictionary.get('V');
        }
        if (typeof widgetValue !== 'string' || typeof annotValue !== 'string' || widgetValue !== annotValue) {
            return false;
        }
        return true;
    }
    _isNode(kids) {
        let isNode = false;
        if (_isNullOrUndefined(kids) && kids.length > 0) {
            const entry = kids[0]; // eslint-disable-line
            let dictionary;
            if (_isNullOrUndefined(entry)) {
                if (entry instanceof _PdfDictionary) {
                    dictionary = entry;
                }
                else if (entry instanceof _PdfReference) {
                    dictionary = this._crossReference._fetch(entry);
                }
            }
            if (dictionary && dictionary.has('Subtype')) {
                const subtype = dictionary.get('Subtype');
                if (subtype && subtype.name !== 'Widget') {
                    isNode = true;
                }
            }
        }
        return isNode;
    }
    _parseWidgetReferences() {
        if (typeof this._widgetReferences === 'undefined' && this.count > 0) {
            this._widgetReferences = [];
            this._fields.forEach((fieldReference) => {
                const dictionary = this._crossReference._fetch(fieldReference);
                if (dictionary) {
                    if (dictionary.has('Kids')) {
                        const fieldKids = dictionary.get('Kids');
                        if (fieldKids && fieldKids.length > 0) {
                            fieldKids.forEach((kidReference) => {
                                let kidDictionary;
                                if (kidReference && kidReference instanceof _PdfDictionary) {
                                    kidDictionary = kidReference;
                                }
                                else if (kidReference && kidReference instanceof _PdfReference) {
                                    kidDictionary = this._crossReference._fetch(kidReference);
                                }
                                if (kidDictionary && kidDictionary.has('Subtype')) {
                                    const subtype = kidDictionary.get('Subtype');
                                    if (subtype && subtype.name === 'Widget') {
                                        this._widgetReferences.push(kidReference);
                                    }
                                }
                            });
                        }
                    }
                    else {
                        this._widgetReferences.push(fieldReference);
                    }
                }
            });
        }
        return this._widgetReferences;
    }
    _doPostProcess(isFlatten, pageToImport) {
        for (let i = this.count - 1; i >= 0; i--) {
            const field = this.fieldAt(i);
            if (field && !field._isLoaded && typeof field._tabIndex !== 'undefined' && field._tabIndex >= 0) {
                const page = field._page;
                if (page &&
                    page._pageDictionary.has('Annots') &&
                    (page.tabOrder === PdfFormFieldsTabOrder.manual || this._tabOrder === PdfFormFieldsTabOrder.manual)) {
                    const annots = page._pageDictionary.get('Annots');
                    const annotationCollection = new PdfAnnotationCollection(annots, this._crossReference, page);
                    page._annotations = annotationCollection;
                    for (let i = 0; i < field.itemsCount; i++) {
                        const item = field.itemAt(i);
                        if (item && item instanceof PdfWidgetAnnotation) {
                            let index = annots.indexOf(item._ref);
                            if (index < 0) {
                                index = field._annotationIndex;
                            }
                            if (index >= 0) {
                                const annotations = page.annotations._reArrange(field._ref, field._tabIndex, index);
                                page._pageDictionary.update('Annots', annotations);
                                page._pageDictionary._updated = true;
                            }
                        }
                    }
                }
            }
            if (field && ((pageToImport && field.page === pageToImport) || !pageToImport)) {
                if (pageToImport) {
                    field._isImport = true;
                }
                field._doPostProcess(isFlatten || field.flatten);
                if (!isFlatten && field.flatten || (isFlatten && pageToImport && field.page === pageToImport)) {
                    this.removeFieldAt(i);
                }
            }
        }
    }
    _getFieldIndex(name) {
        let index = -1;
        if (this.count > 0) {
            if (!this._fieldNames) {
                this._fieldNames = [];
            }
            if (!this._indexedFieldNames) {
                this._indexedFieldNames = [];
            }
            if (!this._actualFieldNames) {
                this._actualFieldNames = [];
            }
            if (!this._indexedActualFieldNames) {
                this._indexedActualFieldNames = [];
            }
            for (let i = 0; i < this.count; i++) {
                const field = this.fieldAt(i);
                if (field) {
                    const fieldName = field.name;
                    if (fieldName) {
                        this._fieldNames.push(fieldName);
                        this._indexedFieldNames.push(fieldName.split('[')[0]);
                    }
                    const actualName = field.actualName;
                    if (actualName) {
                        this._actualFieldNames.push(actualName);
                        this._indexedActualFieldNames.push(actualName.split('[')[0]);
                    }
                }
            }
            let nameIndex = this._fieldNames.indexOf(name);
            if (nameIndex !== -1) {
                index = nameIndex;
            }
            else {
                nameIndex = this._indexedFieldNames.indexOf(name);
                if (nameIndex !== -1) {
                    index = nameIndex;
                }
                else {
                    nameIndex = this._actualFieldNames.indexOf(name);
                    if (nameIndex !== -1) {
                        index = nameIndex;
                    }
                    else {
                        nameIndex = this._indexedActualFieldNames.indexOf(name);
                        if (nameIndex !== -1) {
                            index = nameIndex;
                        }
                    }
                }
            }
        }
        return index;
    }
    _getFields() {
        const fields = [];
        for (let i = 0; i < this._fields.length; i++) {
            const field = this.fieldAt(i);
            if (field && field instanceof PdfField) {
                fields.push(field);
            }
        }
        return fields;
    }
    _getOrder(tabOrder) {
        if (tabOrder !== PdfFormFieldsTabOrder.none) {
            let tabs = '';
            if (tabOrder === PdfFormFieldsTabOrder.row) {
                tabs = 'R';
            }
            else if (tabOrder === PdfFormFieldsTabOrder.column) {
                tabs = 'C';
            }
            else if (tabOrder === PdfFormFieldsTabOrder.structure) {
                tabs = 'S';
            }
            return _PdfName.get(tabs);
        }
        return null;
    }
    _compareFields(field1, field2) {
        let result = 0;
        let xdiff;
        let index;
        const page1 = field1.page;
        const page2 = field2.page;
        if (page1 && !page1._isNew && page1 instanceof PdfPage && page2 && !page2._isNew && page2 instanceof PdfPage) {
            const page1Index = this._sortItemByPageIndex(field1, false)._pageIndex;
            const page2Index = this._sortItemByPageIndex(field2, false)._pageIndex;
            let rectangle1;
            if (field1._dictionary.has('Kids')) {
                rectangle1 = this._getItemRectangle(field1);
            }
            else {
                rectangle1 = this._getRectangle(field1._dictionary);
            }
            let rectangle2;
            if (field2._dictionary.has('Kids')) {
                rectangle2 = this._getItemRectangle(field2);
            }
            else {
                rectangle2 = this._getRectangle(field2._dictionary);
            }
            const firstHeight = rectangle1[3] - rectangle1[1];
            const secondHeight = rectangle2[3] - rectangle2[1];
            if (rectangle1 && rectangle1.length >= 2 && rectangle2 && rectangle2.length >= 2) {
                const x1 = rectangle1[0];
                const y1 = rectangle1[1];
                const x2 = rectangle2[0];
                const y2 = rectangle2[1];
                if (typeof x1 === 'number' && typeof x2 === 'number' &&
                    typeof y1 === 'number' && typeof y2 === 'number') {
                    index = page1Index - page2Index;
                    if (this._tabOrder === PdfFormFieldsTabOrder.row) {
                        xdiff = this._compare(y2, y1);
                        if (xdiff !== 0) {
                            let isValid = xdiff === -1 && y1 > y2 && (y1 - firstHeight / 2) < y2;
                            isValid = isValid || (xdiff === 1 && y2 > y1 && (y2 - secondHeight / 2) < y1);
                            if (isValid) {
                                xdiff = 0;
                            }
                        }
                        if (index !== 0) {
                            result = index;
                        }
                        else if (xdiff !== 0) {
                            result = xdiff;
                        }
                        else {
                            result = this._compare(x1, x2);
                        }
                    }
                    else if (this._tabOrder === PdfFormFieldsTabOrder.column) {
                        xdiff = this._compare(x1, x2);
                        if (index !== 0) {
                            result = index;
                        }
                        else if (xdiff !== 0) {
                            result = xdiff;
                        }
                        else {
                            result = this._compare(y2, y1);
                        }
                    }
                    else if (this._tabOrder === PdfFormFieldsTabOrder.manual ||
                        this._tabOrder === PdfFormFieldsTabOrder.none ||
                        this._tabOrder === PdfFormFieldsTabOrder.structure ||
                        this._tabOrder === PdfFormFieldsTabOrder.widget) {
                        if (field1 instanceof PdfField && field2 instanceof PdfField) {
                            const field1Index = field1.tabIndex;
                            const field2Index = field2.tabIndex;
                            xdiff = this._compare(field1Index, field2Index);
                            if (index !== 0) {
                                result = index;
                            }
                            else {
                                result = xdiff;
                            }
                        }
                    }
                }
            }
        }
        return result;
    }
    _getRectangle(dictionary) {
        let rect;
        if (dictionary && dictionary.has('Rect')) {
            rect = dictionary.getArray('Rect');
        }
        return rect;
    }
    _getItemRectangle(field) {
        let result;
        const dictionary = field._dictionary;
        if (dictionary.has('Kids')) {
            const kids = dictionary.getArray('Kids');
            if (_isNullOrUndefined(kids) && kids.length >= 1) {
                if (kids.length === 1) {
                    result = this._getRectangle(kids[0]);
                }
                else {
                    if (field && field.itemsCount > 1) {
                        result = this._getRectangle(field.itemAt(0)._dictionary);
                    }
                    else {
                        result = this._getRectangle(kids[0]);
                    }
                }
            }
        }
        return result;
    }
    _compare(x, y) {
        if (x > y) {
            return 1;
        }
        else if (x < y) {
            return -1;
        }
        else {
            return 0;
        }
    }
    _compareKidsElement(x, y) {
        const xDictionary = this._crossReference._fetch(x);
        const yDictionary = this._crossReference._fetch(y);
        const xRect = this._getRectangle(xDictionary);
        const yRect = this._getRectangle(yDictionary);
        let result;
        if (xRect && xRect.length >= 2 && yRect && yRect.length >= 2) {
            const x1 = xRect[0];
            const y1 = xRect[1];
            const x2 = yRect[0];
            const y2 = yRect[1];
            if (typeof x1 === 'number' && typeof x2 === 'number' &&
                typeof y1 === 'number' && typeof y2 === 'number') {
                let xdiff;
                if (this._tabOrder === PdfFormFieldsTabOrder.row) {
                    xdiff = this._compare(y2, y1);
                    if (xdiff !== 0) {
                        result = xdiff;
                    }
                    else {
                        result = this._compare(x1, x2);
                    }
                }
                else if (this._tabOrder === PdfFormFieldsTabOrder.column) {
                    xdiff = this._compare(x1, x2);
                    if (xdiff !== 0) {
                        result = xdiff;
                    }
                    else {
                        result = this._compare(y2, y1);
                    }
                }
                else {
                    result = 0;
                }
                return result;
            }
        }
        return result;
    }
    _sortItemByPageIndex(field, hasPageTabOrder) {
        let page = field.page;
        const tabOrder = this._tabOrder;
        this._tabOrder = hasPageTabOrder ? field.page.tabOrder : tabOrder;
        this._sortFieldItems(field);
        if (field._isLoaded && field._kidsCount > 1) {
            page = field.itemAt(0).page;
        }
        this._tabOrder = tabOrder;
        if (typeof page === 'undefined') {
            page = field.page;
        }
        return page;
    }
    _sortFieldItems(field) {
        if (field._isLoaded && (field instanceof PdfTextBoxField ||
            field instanceof PdfListBoxField ||
            field instanceof PdfCheckBoxField ||
            field instanceof PdfRadioButtonListField)) {
            const collection = field._parseItems(); // eslint-disable-line
            collection.sort((item1, item2) => {
                return this._compareFieldItem(item1, item2);
            });
            field._parsedItems.clear();
            collection.forEach((item, i) => {
                field._parsedItems.set(i, item);
            });
        }
    }
    _compareFieldItem(item1, item2) {
        let result = 0;
        if (typeof item1 !== 'undefined' && typeof item2 !== 'undefined') {
            const page1 = item1.page;
            const page2 = item2.page;
            const array1 = this._getRectangle(item1._dictionary);
            const array2 = this._getRectangle(item2._dictionary);
            if (array1 && array2) {
                const x1 = array1[0];
                const y1 = array1[1];
                const x2 = array2[0];
                const y2 = array2[1];
                let xdiff;
                if (this._tabOrder === PdfFormFieldsTabOrder.row) {
                    xdiff = this._compare(page1._pageIndex, page2._pageIndex);
                    if (xdiff !== 0) {
                        result = xdiff;
                    }
                    else {
                        xdiff = this._compare(y2, y1);
                        if (xdiff !== 0) {
                            result = xdiff;
                        }
                        else {
                            result = this._compare(x1, x2);
                        }
                    }
                }
                else if (this._tabOrder === PdfFormFieldsTabOrder.column) {
                    xdiff = this._compare(page1._pageIndex, page2._pageIndex);
                    if (xdiff !== 0) {
                        result = xdiff;
                    }
                    else {
                        xdiff = this._compare(x1, x2);
                        if (xdiff !== 0) {
                            result = xdiff;
                        }
                        else {
                            result = this._compare(y2, y1);
                        }
                    }
                }
            }
        }
        return result;
    }
    _clear() {
        this._fields = [];
        this._parsedFields = new Map();
    }
}
